/*
 * Le Matin — Cake date picker
 * Renders a calendar over the native "Available Dates" <select>.
 * Clicking a day sets the select's value and dispatches `change`, so Dawn's
 * variant engine (price, hidden id input, add-to-cart) keeps working as-is.
 *
 * Variant date labels look like "7 June Sunday" / "31 May Sunday" and carry
 * no year. We resolve the year by matching the embedded weekday to the
 * nearest non-past year, which removes ambiguity.
 */
(function () {
  const MONTHS = {
    january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
    july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
  };
  const WEEKDAYS = {
    sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
    thursday: 4, friday: 5, saturday: 6,
  };
  const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTH_LABELS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  function startOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  // Resolve "7 June Sunday" -> a concrete Date using the weekday hint.
  function resolveDate(value) {
    const m = String(value).trim().match(/^(\d{1,2})\s+([A-Za-z]+)\s+([A-Za-z]+)/);
    if (!m) return null;
    const day = parseInt(m[1], 10);
    const month = MONTHS[m[2].toLowerCase()];
    const weekday = WEEKDAYS[m[3].toLowerCase()];
    if (month === undefined || weekday === undefined) return null;

    const today = startOfDay(new Date());
    const thisYear = today.getFullYear();
    let best = null;
    for (let y = thisYear - 1; y <= thisYear + 2; y++) {
      const cand = new Date(y, month, day);
      if (cand.getMonth() !== month) continue; // invalid day for month
      if (cand.getDay() !== weekday) continue; // weekday must match label
      if (!best) {
        best = cand;
      } else {
        const candFuture = cand >= today;
        const bestFuture = best >= today;
        if (candFuture && !bestFuture) best = cand;
        else if (candFuture === bestFuture &&
          Math.abs(cand - today) < Math.abs(best - today)) best = cand;
      }
    }
    return best;
  }

  function dateKey(d) {
    return d.getFullYear() + '-' + d.getMonth() + '-' + d.getDate();
  }

  class CakeDatePicker {
    constructor(root) {
      this.root = root;
      this.select = document.querySelector(root.dataset.selectSelector);
      const dataEl = root.querySelector('[data-cake-dates]');
      if (!this.select || !dataEl) return;

      let raw = [];
      try { raw = JSON.parse(dataEl.textContent); } catch (e) { return; }

      this.byKey = {};
      this.dates = [];
      raw.forEach((v) => {
        const d = resolveDate(v.value);
        if (!d) return;
        const entry = { date: d, value: v.value, id: v.id, available: !!v.available };
        this.byKey[dateKey(d)] = entry;
        this.dates.push(entry);
      });
      this.dates.sort((a, b) => a.date - b.date);
      if (!this.dates.length) return;

      this.today = startOfDay(new Date());
      const leadDays = Math.max(0, parseInt(root.dataset.leadDays || '1', 10) || 1);
      this.minDate = new Date(this.today);
      this.minDate.setDate(this.minDate.getDate() + leadDays);
      const firstAvail = this.dates.find((e) => e.available && e.date >= this.minDate)
        || this.dates.find((e) => e.date >= this.minDate)
        || this.dates[0];
      this.viewYear = firstAvail.date.getFullYear();
      this.viewMonth = firstAvail.date.getMonth();
      this.selectedKey = null;

      this.monthEl = root.querySelector('[data-cake-month]');
      this.gridEl = root.querySelector('[data-cake-grid]');
      this.selectedTextEl = root.querySelector('[data-cake-selected]');
      this.prevBtn = root.querySelector('[data-cake-prev]');
      this.nextBtn = root.querySelector('[data-cake-next]');

      this.prevBtn.addEventListener('click', () => this.shiftMonth(-1));
      this.nextBtn.addEventListener('click', () => this.shiftMonth(1));

      this.minMonth = this.dates[0].date;
      this.maxMonth = this.dates[this.dates.length - 1].date;
      this.disableCutoffOptions();

      this.render();
      // Pre-select the earliest available upcoming date after the cutoff.
      if (firstAvail.available && firstAvail.date >= this.minDate) {
        this.selectDate(firstAvail, false);
      }

      this.initAccordion();
    }

    initAccordion() {
      const details = this.root.querySelector('details');
      if (!details) return;
      const content = details.querySelector('.accordion__content');
      const summary = details.querySelector('summary');
      if (!content || !summary) return;

      const DURATION = 500;
      const ACCORDION_EVENT = 'lematin:accordion-open';
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let animating = false;

      const clear = () => {
        content.style.height = '';
        content.style.overflow = '';
        content.style.transition = '';
        content.style.opacity = '';
        content.style.paddingTop = '';
        content.style.paddingBottom = '';
        animating = false;
      };

      /* Vertical padding has to be animated alongside the height. With
         box-sizing:border-box a `height:0` element still renders at least as tall
         as its padding, so leaving the padding in place makes the separator snap
         by the padding amount at the very start/end of the animation. */
      const readPadding = () => {
        const cs = getComputedStyle(content);
        return { top: cs.paddingTop, bottom: cs.paddingBottom };
      };

      const transition = () =>
        'height ' + DURATION + 'ms ease-in-out, ' +
        'padding-top ' + DURATION + 'ms ease-in-out, ' +
        'padding-bottom ' + DURATION + 'ms ease-in-out, ' +
        'opacity ' + DURATION + 'ms ease-in-out';

      /* Broadcast so the other product-page accordions collapse — only one open
         at a time. */
      const announceOpen = () => {
        document.dispatchEvent(new CustomEvent(ACCORDION_EVENT, { detail: { source: details } }));
      };

      const expand = () => {
        announceOpen();
        animating = true;
        details.open = true;
        const pad = readPadding();
        const target = content.scrollHeight;
        content.style.overflow = 'hidden';
        content.style.height = '0px';
        content.style.paddingTop = '0px';
        content.style.paddingBottom = '0px';
        content.style.opacity = '0';
        requestAnimationFrame(() => {
          content.style.transition = transition();
          content.style.height = target + 'px';
          content.style.paddingTop = pad.top;
          content.style.paddingBottom = pad.bottom;
          content.style.opacity = '1';
        });
        const done = (e) => {
          if (e.propertyName !== 'height') return;
          content.removeEventListener('transitionend', done);
          clear();
        };
        content.addEventListener('transitionend', done);
      };

      const collapse = () => {
        animating = true;
        content.style.overflow = 'hidden';
        content.style.height = content.scrollHeight + 'px';
        content.style.opacity = '1';
        requestAnimationFrame(() => {
          content.style.transition = transition();
          content.style.height = '0px';
          content.style.paddingTop = '0px';
          content.style.paddingBottom = '0px';
          content.style.opacity = '0';
        });
        const done = (e) => {
          if (e.propertyName !== 'height') return;
          content.removeEventListener('transitionend', done);
          details.open = false;
          clear();
        };
        content.addEventListener('transitionend', done);
      };

      summary.addEventListener('click', (e) => {
        if (reduceMotion) {
          if (!details.open) announceOpen();
          return;
        }
        e.preventDefault();
        if (animating) return;
        if (details.open) collapse();
        else expand();
      });

      document.addEventListener(ACCORDION_EVENT, (e) => {
        if (e.detail.source === details || !details.open) return;
        if (reduceMotion) { details.open = false; return; }
        if (animating) return;
        collapse();
      });
    }

    disableCutoffOptions() {
      const liveSelect = document.querySelector(this.root.dataset.selectSelector);
      if (liveSelect) this.select = liveSelect;
      if (!this.select || !this.select.options) return;

      Array.from(this.select.options).forEach((option) => {
        const date = resolveDate(option.value);
        if (date && date < this.minDate) option.disabled = true;
      });
    }

    shiftMonth(delta) {
      let m = this.viewMonth + delta;
      let y = this.viewYear;
      if (m < 0) { m = 11; y--; }
      if (m > 11) { m = 0; y++; }
      this.viewMonth = m;
      this.viewYear = y;
      this.render();
    }

    canGoPrev() {
      return new Date(this.viewYear, this.viewMonth, 1) >
        new Date(this.minMonth.getFullYear(), this.minMonth.getMonth(), 1);
    }

    canGoNext() {
      return new Date(this.viewYear, this.viewMonth, 1) <
        new Date(this.maxMonth.getFullYear(), this.maxMonth.getMonth(), 1);
    }

    render() {
      this.monthEl.textContent = MONTH_LABELS[this.viewMonth] + ' ' + this.viewYear;
      this.prevBtn.disabled = !this.canGoPrev();
      this.nextBtn.disabled = !this.canGoNext();

      this.gridEl.innerHTML = '';
      DOW_LABELS.forEach((d) => {
        const el = document.createElement('div');
        el.className = 'cake-dp__dow';
        el.textContent = d;
        this.gridEl.appendChild(el);
      });

      const firstDow = new Date(this.viewYear, this.viewMonth, 1).getDay();
      const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();

      for (let day = 1; day <= daysInMonth; day++) {
        const d = new Date(this.viewYear, this.viewMonth, day);
        const key = dateKey(d);
        const entry = this.byKey[key];
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cake-dp__day';
        // Anchor the first rendered day to its true weekday column so the grid
        // can never drift, regardless of leading blank-cell counting.
        if (day === 1) btn.style.gridColumnStart = String(firstDow + 1);
        btn.textContent = day;

        if (!entry || d < this.minDate) {
          btn.classList.add('cake-dp__day--disabled');
          btn.disabled = true;
        } else if (!entry.available) {
          btn.classList.add('cake-dp__day--soldout');
          btn.disabled = true;
          btn.title = 'Sold out';
        } else {
          btn.classList.add('cake-dp__day--available');
          if (key === this.selectedKey) btn.classList.add('cake-dp__day--selected');
          btn.addEventListener('click', () => this.selectDate(entry, true));
        }
        this.gridEl.appendChild(btn);
      }
    }

    selectDate(entry, userInitiated) {
      this.selectedKey = dateKey(entry.date);
      // Drive the native select so Dawn updates the variant.
      // Dawn replaces the <select> inside <variant-selects> on every variant
      // change (incl. this picker's own on-load pre-selection), so the
      // reference captured at init goes stale and later writes hit a detached
      // node. Re-query the live element each time before driving it. For gelato
      // the target is a hidden property input outside <variant-selects>, so this
      // simply returns the same node — harmless.
      const liveSelect = document.querySelector(this.root.dataset.selectSelector);
      if (liveSelect) this.select = liveSelect;
      if (this.select && this.select.value !== entry.value) {
        this.select.value = entry.value;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const label = entry.value.replace(/\s+([A-Za-z]+)$/, ', $1');
      if (this.selectedTextEl) this.selectedTextEl.textContent = label;
      this.render();
    }

  }

  function init() {
    document.querySelectorAll('[data-cake-date-picker]').forEach((root) => {
      if (root.dataset.cakeInit) return;
      root.dataset.cakeInit = '1';
      new CakeDatePicker(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-init after Shopify section re-renders (theme editor / variant swaps).
  document.addEventListener('shopify:section:load', init);
})();
